/**
 * @module app.um
 */

import { autoinject, TaskQueue } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { ExtendLocalState, applyLocalState } from 'app/shared/models/local-state-extensions';
import { apiCall } from 'app/shared/lib/api/api';
import { AuthService, IAuthStatusSearchFilters } from 'app/um/services/auth-service';
import { I18N } from 'aurelia-i18n';
import { Toaster } from 'app/shared/lib/toaster';
import { EssTableCustomElement, ExpandEvent } from 'app/shared/elements/ess-table';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IPopup } from 'app/shared/elements/popup-base';
import { IQueryLoadOptions, IApiActionResults } from 'app/shared/lib/api/api-types';
import { safe } from 'app/shared/lib/utilities';
import { AuthSelectCustomEvent, OpenAuthQueuesCustomEvent } from 'app/um/components/auth/auth-owner-auths-grid';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { RouteConfig, NavigationInstruction } from 'aurelia-router';
import { AssignAuthsPageContext } from 'app/um/lib/assign-auths-context';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import { AuthQueueHistoryGrid } from 'app/um/components/auth/auth-queue-history-grid';
import { AuthWithLocalState } from 'app/um/models/local-state-extensions';
import * as UM from 'app/um/models/essette-api';
import { guid } from 'app/shared/lib/guid';


interface IAuthOwnerLocalState { isLoaded?: boolean; }
type AuthOwnerWithLocalState = ExtendLocalState<UM.AuthOwner, IAuthOwnerLocalState>;

const expandedOwnersCache = new Map<AssignAuthsPage, ReadonlyArray<UM.AuthOwner>>();

@autoinject()
export class AssignAuthsPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected owners: ReadonlyArray<AuthOwnerWithLocalState> = [];
    protected statuses: ReadonlyArray<UM.AuthStatus> = [];
    protected ownersWithAuths: ReadonlyArray<AuthOwnerWithLocalState> = [];

    protected selectedUser: UM.AuthOwner | null = null;
    protected selectedStatus: UM.AuthStatusWithReasonModel | null = null;
    protected selectedStatusReason: UM.AuthStatusReason | null = null;
    protected selectedCount: number = 0;
    protected authQueueHistory: UM.Auth | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    protected pageSize: number = 10000;
    protected authPageSize: number = 20;

    protected hasAuthOwner: boolean = false;
    protected hasAuthAssign: boolean = false;

    protected invalidCount: number = 0;
    private verifyState: number = 0;
    private verifiedAuths: AuthWithLocalState[] = [];

    protected id: guid = guid();


    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<UM.AuthOwner>;
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $queueHistory!: AuthQueueHistoryGrid;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly context: AssignAuthsPageContext,
        private readonly service: AuthService,
        private readonly queue: TaskQueue,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected async canActivate(params: any, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<boolean> {
        await this.accountContext.load();
        return this.accountContext.hasRouteAccess(navInstruction.config.name!);
    }

    protected async activate(params: any, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<void> {
        this.checkPermissions();
        this.loadAuthStatuses();
        this.reload();
    }


    ////****************** Events ***************************************************////
    protected trackExpanded({ detail }: ExpandEvent<AuthOwnerWithLocalState>): void {
        if(detail.items) { expandedOwnersCache.set(this, detail.items); }
        if(detail.item) {
            const expanded = this.$table.isExpanded(detail.item);
            if(!detail.item._state) {
                detail.item._state = { isLoaded: expanded }
            } else {
                const isLoaded = safe(() => detail.item!._state!.isLoaded);
                if(!isLoaded) {
                    detail.item._state.isLoaded = this.$table.isExpanded(detail.item);
                }
            }
        }
    }

    // fired when a new user is selected
    protected assignmentChanged(event: Event) {
        // clear "isValid" flag for every auth since a new user has been selected
        applyLocalState(this.verifiedAuths, { isValid: null }, {});
        this.verifiedAuths = [];

        // verify assignment of selected auths
        this.verifyAssignment();
    }

    protected authsLoaded({ detail }: AuthSelectCustomEvent): void {
        // new cases loaded, synchronize "isValid" state from previously verified cases
        for(const item of detail.auths) {
            const target = this.verifiedAuths.find(a => a.id === item.id);
            // if there is a selected item, copy the isValid value, otherwise, default to null
            let isValid =
                !target ? null : (target._state && target._state.isValid);
            applyLocalState([item as AuthWithLocalState], { isValid }, {});
        }
    }

    protected authsSelected({ detail }: AuthSelectCustomEvent): void {
        if(detail.auths.length === 0) {
            this.context.clearSelectedAuths(detail.ownerId)
        }
        else {
            this.context.setSelectedAuths(detail.ownerId, <Array<UM.Auth>>detail.auths);
        }

        this.selectedCount = this.context.getSelectedAuths().length;

        // verify assignment of selected auths
        this.verifyAssignment();
    }

    protected openAuthQueueHistory({ detail }: OpenAuthQueuesCustomEvent) {
        if(detail.auth && detail.auth.id) {
            this.authQueueHistory = detail.auth;
            this.$queueHistory.authId = detail.auth.id;
            this.$modal.open();
        }
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const sorting = this.sorting;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        this.loadOwnersWithAuths();
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasAuthOwner = this.accountContext.hasPermission('UM.Auth.Duty.Owner');
        this.hasAuthAssign = this.accountContext.hasPermission('UM.Auth.Assign');

        if(!this.hasAuthAssign) {
            this.logger.warn('User does not have appropriate permissions to assign auths');
        }
    }

    private async reload(): Promise<void> {
        this.selectedStatus = this.selectedStatusReason = null;
        this.selectedUser = null;
        this.loadActiveAuthOwners();
        this.loadOwnersWithAuths();
        // Close all detailed rows to force reload the owner case tables after assigning auths
        if(this.$table) {
            this.$table.closeAll();
        }
    }

    private restoreExpanded() {
        // we need to give aurelia a chance to bind the goals into the
        // table before we ask the table to expand the rows
        this.queue.queueTask(() => {
            const expandedOwners = expandedOwnersCache.get(this);
            if(expandedOwners !== undefined) {
                expandedOwners.forEach((owner) => {
                    const foundOwner = this.ownersWithAuths.find((o) => owner.id === o.id);
                    if(!!foundOwner) {
                        this.$table.open(foundOwner);
                    }
                });
            }
        });
    }

    private isVerifiedAuth(item: AuthWithLocalState): boolean {
        const result = (item._state && item._state.isValid !== undefined && item._state.isValid !== null) || false;
        return result;
    }

    private calculateInvalidCount(): void {
        const selected = this.context.getSelectedAuths();
        let count = 0;
        for(const selectedAuth of selected) {
            if(selectedAuth._state && selectedAuth._state.isValid === false) {
                count++;
            }
        }
        this.invalidCount = count;
    }

    private async verifyAssignment(): Promise<void> {
        try {
            this.state.beginProcessing();

            // keep track of most recent verification attempt
            const previousState = ++this.verifyState;

            // get selected cases except for ones that have already been verified
            const selected = this.context.getSelectedAuths().filter(a => !this.isVerifiedAuth(a));
            const auths = getReferences(selected);

            // make sure there's actually work to do
            if(/* cm only this.choice !== 'assign' ||*/ !this.selectedUser || !this.selectedUser.id || !auths || !auths.length) {
                this.invalidCount = 0;
                return;
            }

            const response = await this.service.verifyAssignment(this.selectedUser.id, auths);

            if(previousState !== this.verifyState) {
                this.logger.info(this.i18n.tr('ui:messages.warnings.verificationInterruptionDiscardResults'));
            }
            else {
                // only stash results if another newer request has not been made
                if(response.isSuccessful && response.result.filter && response.result.filter.items) {
                    // set all 'isValid' to false
                    applyLocalState(selected, { isValid: false }, {});
                    for(const item of response.result.filter.items) {
                        const found = selected.find(a => a.id === item.id);
                        if(found) {
                            // switch 'isValid' to true
                            applyLocalState([found], { isValid: true }, {});
                        }
                    }
                    // keep a list of all verified cases so we don't check them again
                    this.verifiedAuths.push(...selected);
                }
            }
        }
        finally {
            this.state.endProcessing();
            this.calculateInvalidCount();
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAuthStatuses(): Promise<void> {
        const includes: string[] = ['reasons'];
        const searchFilters: IAuthStatusSearchFilters = {
            isOpen: false,
            isApproval: false
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadAuthStatuses({ all: true }, includes, searchFilters);
            this.statuses = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    /** Loads active auth owners for the "Assign to" drop down */
    @apiCall
    private async loadActiveAuthOwners(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };
        const includes: string[] = [];

        try {
            this.state.beginLoad();
            const response = await this.service.loadAuthOwners(options, includes);
            this.owners = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadOwnersWithAuths(): Promise<void> {
        this.context.clear();
        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting,
            includeInactive: true
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadAuthOwners(options, [], true, 'includeUnassigned');
            const results: AuthOwnerWithLocalState[] = response.results
                .map(r => {
                    (<AuthOwnerWithLocalState>r)._state = { isLoaded: false }
                    return r;
                });

            this.ownersWithAuths = results;
            this.resultCount = response.count || 0;
            this.restoreExpanded();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async assignAuths(): Promise<void> {
        if(!this.selectedUser || !this.selectedUser.id) {
            this.logger.error('Selected user is not set!');
            this.toaster.error().message('Selected User not set!').show();
            return;
        }

        // make sure verification has finished running
        await this.verifyAssignment();
        if(this.invalidCount > 0) {
            return;
        }

        const promises = new Array<Promise<IApiActionResults>>();
        this.context.getSelectedAuths()
            .map(a => {
                const returnAuth: UM.Auth = {
                    id: a.id,
                    assignedTo: getReference(this.selectedUser)
                };

                if(!!a.portalQueue?.id) {
                    returnAuth.removeFromPortalQueue = true;
                }

                return returnAuth;
            })
            .forEach(a => {
                promises.push(this.service.assignAuth(a));
            });

        try {
            this.state.beginSave();
            const responses = await Promise.all(promises);
            const success = responses.every(r => r.isSuccessful);
            this.toaster.standard.assigned('um:auth.$', success, responses.length);
            if(success) { this.reload(); }
        } catch(error) {
            this.toaster.standard.assigned('um:auth.$', false, promises.length);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    protected async declineAuths(): Promise<void> {
        if(!this.selectedStatus || !this.selectedStatusReason) {
            return;
        }

        const promises: Promise<IApiActionResults>[] = [];
        this.context.getSelectedAuths()
            .map(a => {
                return {
                    id: a.id,
                    status: getReference(this.selectedStatus, {
                        reason: getReference(this.selectedStatusReason)
                    })
                } as UM.Auth
            })
            .forEach(a => {
                promises.push(this.service.saveAuthStatus(a));
            });

        try {
            this.state.beginSave();
            // Execute all promises and wait until they all finish
            const responses = await Promise.all(promises);
            const success = responses.every(r => r.isSuccessful);
            this.toaster.standard.updated('um:auth.$', success, responses.length);
            if(success) { this.reload(); }
        }
        catch(error) {
            this.toaster.standard.updated('um:auth.$', false, promises.length);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }
}
